import { History } from 'lucide-react';
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  canonicalDecisionKeys,
  DECISION_OTHER_KEY,
  type DecisionQuestionView,
  MAX_DECISION_NOTE,
  splitDecisionKeys,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { noteRequired, stampDayTime } from '../model';
import { respondentLabel } from '../respondent';
import { AnsweredPill, OriginPill, RecommendedPill } from './labels';

/** Ngừng gõ bao lâu thì lưu ghi chú — plan §4.3 (trang mẫu 400 ms là một event mỗi lần ngừng gõ). */
export /**
 * Vành lóe của ô chọn kính đi theo con trỏ trong **cả khung lựa chọn** (người
 * dùng 2026-10-07): chuột ở đâu trong khung thì đốm sáng nằm ở mép ô phía ấy
 * (chiếu từ tâm ô ra mép), vào tới ô thì đốm đi theo con trỏ. Độ sáng do CSS
 * lo: nhẹ khi ở khung, mạnh khi ở ô. Ghi thẳng biến CSS, không qua state —
 * mỗi lần chuột nhích là một sự kiện.
 */
function aimGlassCheck(event: ReactPointerEvent<HTMLElement>): void {
  if (event.pointerType !== 'mouse') return;
  const check = event.currentTarget.querySelector<HTMLElement>('.glass-check');
  if (!check) return;
  const box = check.getBoundingClientRect();
  const cx = box.width / 2;
  const cy = box.height / 2;
  const dx = event.clientX - (box.left + cx);
  const dy = event.clientY - (box.top + cy);
  const distance = Math.hypot(dx, dy) || 1;
  const radius = Math.min(cx, cy);
  const k = distance <= radius ? 1 : radius / distance;
  check.style.setProperty('--glow-x', `${cx + dx * k}px`);
  check.style.setProperty('--glow-y', `${cy + dy * k}px`);
}
function releaseGlassCheck(event: ReactPointerEvent<HTMLElement>): void {
  const check = event.currentTarget.querySelector<HTMLElement>('.glass-check');
  check?.style.removeProperty('--glow-x');
  check?.style.removeProperty('--glow-y');
}
const glassCheckRowHandlers = {
  onPointerMove: aimGlassCheck,
  onPointerLeave: releaseGlassCheck,
} as const;

const NOTE_SAVE_DELAY_MS = 1_500;

/**
 * Một câu hỏi của sổ — APM-DSHEET-c, bố cục theo trang mẫu (plan §2).
 *
 * Mỗi câu **một khối có viền**; mỗi phương án là một **thẻ bấm cả khối**
 * (`<label>` bọc ô radio) — ở 390px không ai nhắm trúng một ô tròn 16px.
 *
 * ## Ghi chú
 *
 * Lưu khi rời ô hoặc sau 1,5 s không gõ, và **không gửi** khi khoá và ghi chú
 * không đổi — mỗi lần gửi là một event `answered`. Ghi chú nháp không bị lần
 * refetch 15 s đè lên: chỉ nhận bản server khi người dùng chưa sửa gì.
 *
 * ## "Phương án khác"
 *
 * Chọn `X` mà chưa có ghi chú thì **chưa gửi** (server sẽ trả 400): thẻ sáng
 * lên, ô ghi chú mở và nói rõ cần ghi chú — gõ xong mới lưu.
 *
 * Cha lo mutation và `baseRev`; thẻ chỉ báo "muốn chốt khoá này với ghi chú
 * này". `saving` khoá thẻ trong lúc một lần lưu đang bay, để hai lần lưu nối
 * nhau không mang cùng một `baseRev` rồi tự đâm vào nhau thành 409.
 */
export function DecisionQuestionCard({
  question,
  allowOther,
  locked,
  saving,
  conflictToken = 0,
  glass = false,
  onAnswer,
  onClear,
  onHistory,
}: {
  readonly question: DecisionQuestionView;
  readonly allowOther: boolean;
  /**
   * Ô chọn kiểu kính mờ (`ios-check-glass`) với vành lóe sáng theo con trỏ —
   * **chỉ** ở hai chỗ mặc bộ đồ kính: link công khai và "Mở trong cửa sổ mới"
   * (người dùng chốt 2026-10-07). Trang sổ trong app giữ ô `.ios-check` thường.
   */
  readonly glass?: boolean;
  /** Không phải người chốt / sổ không mở / đang xem quá khứ. */
  readonly locked: boolean;
  readonly saving: boolean;
  /**
   * Tăng mỗi lần lưu câu này dính 409. Đổi ⇒ bỏ nháp, nhận đáp án server: nếu
   * giữ nháp thì lần lưu kế tiếp tự gửi ghi chú của mình lên **khoá của người
   * kia** — ghi đè im lặng đúng thứ khoá lạc quan sinh ra để chặn.
   */
  readonly conflictToken?: number;
  readonly onAnswer: (key: string, note: string) => void;
  readonly onClear: () => void;
  /** Bỏ trống ⇒ không có nút Lịch sử (đang xem quá khứ). */
  readonly onHistory?: (() => void) | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const answer = question.answer;
  const serverNote = answer?.note ?? '';

  /** `X` đã bấm nhưng chưa có ghi chú — chưa gửi. */
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState(serverNote);

  // Nhận bản server khi nó đổi (refetch, xung đột) — trừ khi người dùng đang sửa dở.
  const [seenNote, setSeenNote] = useState(serverNote);
  const [seenRev, setSeenRev] = useState(question.answerRev);
  if (serverNote !== seenNote) {
    setSeenNote(serverNote);
    if (draft === seenNote) setDraft(serverNote);
  }
  const [seenConflict, setSeenConflict] = useState(conflictToken);
  if (conflictToken !== seenConflict) {
    setSeenConflict(conflictToken);
    setDraft(serverNote);
    setPendingKey(null);
  }
  if (question.answerRev !== seenRev) {
    setSeenRev(question.answerRev);
    setPendingKey(null);
  }

  /*
    Lựa chọn vừa bấm, hiện ngay trong lúc lần lưu đang bay — chỉ để **vẽ**, không
    đi vào `commit`: lưu hỏng (409, mất mạng) thì nó rơi đi và thẻ về đúng đáp án
    của server, thay vì gửi lại một lựa chọn server vừa từ chối.
  */
  const [optimisticKey, setOptimisticKey] = useState<string | null>(null);
  const wasSaving = useRef(saving);
  useEffect(() => {
    if (wasSaving.current && !saving) setOptimisticKey(null);
    wasSaving.current = saving;
  }, [saving]);
  const selectedKey = pendingKey ?? optimisticKey ?? answer?.key ?? null;
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const commit = (): void => {
    if (locked || saving) return;
    const key = pendingKey ?? answer?.key ?? null;
    if (!key || noteRequired(key, draft)) return;
    const note = draft.trim();
    if (answer && key === answer.key && note === answer.note.trim()) return;
    onAnswer(key, note);
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    const timer = setTimeout(() => commitRef.current(), NOTE_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  // Lần lưu trước vừa xong ⇒ gửi phần ghi chú gõ thêm trong lúc chờ (nếu có).
  useEffect(() => {
    if (!saving) commitRef.current();
  }, [saving]);

  const multiple = question.multiple;
  /** Các mã đang chọn — câu chọn một thì tối đa một mã. */
  const selectedKeys = new Set(splitDecisionKeys(selectedKey));

  const choose = (option: string): void => {
    if (locked || saving) return;
    /*
      Câu chọn nhiều (APM-DSX-multi): bấm là bật/tắt **mã đó** trong tập đang
      chọn, rồi gửi cả tập ở dạng chuẩn ("A,C,X"). Bỏ hết thì là bỏ chốt.
    */
    let key = option;
    if (multiple) {
      const next = new Set(selectedKeys);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      if (next.size === 0) {
        clear();
        return;
      }
      key = canonicalDecisionKeys([...next]);
    }
    if (noteRequired(key, draft)) {
      setPendingKey(key);
      // Sau khi ô ghi chú hiện ra.
      setTimeout(() => noteRef.current?.focus(), 0);
      return;
    }
    setPendingKey(null);
    if (answer && answer.key === key) return;
    setOptimisticKey(key);
    onAnswer(key, draft.trim());
  };

  /*
    Câu chọn một: bấm lại đúng lựa chọn đang chọn là **huỷ** nó (người dùng
    2026-10-07). Radio không bắn `change` khi đã được chọn, nên bắt ở `click` —
    đọc `selectedKeys` của lần vẽ trước bấm, tức lựa chọn cũ. "Phương án khác"
    đang chờ ghi chú thì bấm lại chỉ bỏ trạng thái chờ, không đụng đáp án đã lưu.
  */
  const toggleOff = (option: string): void => {
    if (multiple || locked || saving || !selectedKeys.has(option)) return;
    if (pendingKey === option) {
      setPendingKey(null);
      return;
    }
    clear();
  };

  const clear = (): void => {
    setPendingKey(null);
    if (answer) onClear();
  };

  const options = [
    ...question.options.map((option) => ({ ...option, other: false })),
    ...(allowOther || answer?.key === DECISION_OTHER_KEY
      ? [
          {
            key: DECISION_OTHER_KEY,
            title: t('decisionSheet.other'),
            detail: t('decisionSheet.otherDetail'),
            recommended: false,
            other: true,
          },
        ]
      : []),
  ];
  const needsNote = selectedKey !== null && noteRequired(selectedKey, draft);
  const groupName = `decision-${question.id}`;

  return (
    <article
      className="flex min-w-0 flex-col gap-3 rounded-DEFAULT border border-border bg-card p-4 max-sm:p-3"
      data-testid={`decision-question-${question.code}`}
    >
      <header className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <span className="shrink-0 rounded-sm bg-muted px-1.5 font-mono text-xs leading-5 text-muted-foreground">
          {question.code}
        </span>
        <h4 className="min-w-0 flex-1 basis-48 text-base font-semibold leading-6 [overflow-wrap:anywhere]">
          {question.title}
        </h4>
        <span className="flex flex-wrap items-center gap-1">
          {question.origin ? <OriginPill origin={question.origin} /> : null}
          <AnsweredPill answered={answer !== null} />
          {onHistory ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2"
              onClick={onHistory}
              title={t('decisionSheet.history')}
            >
              <History aria-hidden />
              <span className="max-sm:sr-only">{t('decisionSheet.history')}</span>
            </Button>
          ) : null}
        </span>
      </header>

      {question.sourceRef || question.context ? (
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
          {question.sourceRef ? (
            <>
              <dt className="text-muted-foreground">{t('decisionSheet.sourceRef')}</dt>
              <dd className="whitespace-pre-line [overflow-wrap:anywhere]">{question.sourceRef}</dd>
            </>
          ) : null}
          {question.context ? (
            <>
              <dt className="text-muted-foreground">{t('decisionSheet.context')}</dt>
              <dd className="whitespace-pre-line [overflow-wrap:anywhere]">{question.context}</dd>
            </>
          ) : null}
        </dl>
      ) : null}

      {question.asks.length > 0 ? (
        <div className="text-sm">
          <p className="font-medium">{t('decisionSheet.asks')}</p>
          <ol className="ml-5 list-decimal space-y-0.5">
            {question.asks.map((ask, index) => (
              <li key={index} className="[overflow-wrap:anywhere]">
                {ask}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {multiple ? (
        <p className="text-xs text-muted-foreground" data-testid="decision-multiple-hint">
          {t('decisionSheet.multipleHint')}
        </p>
      ) : null}
      <div
        role={multiple ? 'group' : 'radiogroup'}
        aria-label={t('decisionSheet.options', { code: question.code })}
        className="flex flex-col gap-2"
      >
        {options.map((option) => {
          const checked = selectedKeys.has(option.key);
          return (
            <label
              key={option.key}
              className={cn(
                // Nút tích và chữ cái mã nằm **giữa** khung theo chiều dọc, thẳng hàng
                // với nhau (người dùng 2026-10-07) — kể cả khi phương án có dòng mô tả.
                'flex min-w-0 items-center gap-3 rounded-DEFAULT border px-3 py-2 text-sm transition-colors',
                checked ? 'border-primary bg-primary/10' : 'border-border bg-background',
                locked ? 'cursor-default' : 'cursor-pointer hover:bg-muted/50',
                option.other && !checked && 'border-dashed',
                glass && 'glass-check-row',
              )}
              {...(glass ? glassCheckRowHandlers : {})}
            >
              {/*
                Span bọc ngoài mang vành lóe sáng theo con trỏ (`.glass-check::before`;
                `aimGlassCheck` trên khung lựa chọn đặt `--glow-x/--glow-y`):
                `::before`/`::after` của chính input đã dùng cho núm. Không kính thì
                span chỉ là khung trong suốt.
              */}
              <span
                className={cn(
                  'relative inline-flex shrink-0',
                  glass && 'glass-check',
                  glass && multiple && 'glass-check--square',
                )}
              >
                <input
                  type={multiple ? 'checkbox' : 'radio'}
                  name={groupName}
                  value={option.key}
                  checked={checked}
                  /*
                    Chỉ khoá thật khi `locked`. Lúc đang lưu (`saving`) thì KHÔNG
                    `disabled`: ô bị khoá có kiểu mờ 45%, nên mỗi lần bấm cả câu mờ
                    đi rồi sáng lại (người dùng 2026-10-07: "nháy toàn bộ các nút
                    tích"). Bấm trong lúc lưu vẫn bị chặn — `choose`/`toggleOff`
                    bỏ qua khi `saving`, và radio/checkbox là controlled nên
                    React trả lại đúng trạng thái.
                  */
                  disabled={locked}
                  aria-disabled={saving || undefined}
                  onChange={() => choose(option.key)}
                  onClick={() => toggleOff(option.key)}
                  // Cùng ô tích như chọn việc ở danh sách việc (`ios-check-select`):
                  // nền loang từ tâm rồi dấu tích vẽ từ trái. Không dùng bản vàng —
                  // vàng chỉ có nghĩa "đã xong", còn đây là một lựa chọn. Câu chọn
                  // nhiều là ô **vuông bo góc** (`ios-check-square`) — quy ước chung:
                  // tròn = chọn một, vuông = chọn nhiều.
                  className={cn(
                    'ios-check ios-check-select',
                    multiple && 'ios-check-square',
                    glass && 'ios-check-glass',
                  )}
                />
              </span>
              <span className="w-5 shrink-0 text-center font-mono font-semibold leading-none">
                {option.key}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-medium [overflow-wrap:anywhere]">{option.title}</span>
                  {option.recommended ? <RecommendedPill /> : null}
                </span>
                {option.detail ? (
                  <span className="text-muted-foreground [overflow-wrap:anywhere]">
                    {option.detail}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>

      {selectedKey !== null ? (
        <div className="flex flex-col gap-1.5">
          {locked ? (
            answer?.note ? (
              <p className="whitespace-pre-line text-sm [overflow-wrap:anywhere]">
                <span className="text-muted-foreground">{t('decisionSheet.note')}: </span>
                {answer.note}
              </p>
            ) : null
          ) : (
            <>
              <label className="sr-only" htmlFor={`${groupName}-note`}>
                {t('decisionSheet.note')}
              </label>
              <textarea
                id={`${groupName}-note`}
                ref={noteRef}
                value={draft}
                maxLength={MAX_DECISION_NOTE}
                rows={2}
                placeholder={t(
                  selectedKeys.has(DECISION_OTHER_KEY)
                    ? 'decisionSheet.otherNotePlaceholder'
                    : 'decisionSheet.notePlaceholder',
                )}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                aria-invalid={needsNote || undefined}
                className={cn(
                  'w-full resize-y border bg-background px-3 py-2 text-sm',
                  needsNote ? 'border-destructive' : 'border-input',
                )}
              />
              {needsNote ? (
                <p role="alert" className="text-xs text-destructive">
                  {t('decisionSheet.otherNeedsNote')}
                </p>
              ) : null}
            </>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {answer ? (
              <span>
                {answer.imported
                  ? t('decisionSheet.traceImported', {
                      key: answer.key,
                      at: stampDayTime(answer.answeredAt),
                    })
                  : answer.respondent
                    ? // Người ngoài qua link công khai (APM-DSX-b, plan §11.3).
                      t('decisionSheet.traceLink', {
                        key: answer.key,
                        at: stampDayTime(answer.answeredAt),
                        who: respondentLabel(answer.respondent),
                        via: t(
                          answer.respondent.verified
                            ? 'decisionSheet.respondent.viaLink'
                            : 'decisionSheet.respondent.viaLinkUnverified',
                        ),
                      })
                    : t('decisionSheet.trace', {
                        key: answer.key,
                        at: stampDayTime(answer.answeredAt),
                        name: answer.answeredBy?.name ?? t('decisionSheet.someone'),
                      })}
              </span>
            ) : null}
            {!locked ? (
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto h-7"
                disabled={saving}
                onClick={clear}
              >
                {t('decisionSheet.clear')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
